import NorthRoundedIcon from "@mui/icons-material/NorthRounded";
import { Box, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useAPI from "../../../../../api/hooks/useAPI";
import useColors from "../../../../../api/hooks/useColors";
import { stepKeys } from "../../../../../api/queryKeys";
import CustomToolTip from "../../../../../components/CustomTooltip";
import StyledDataGrid from "../../../../../components/StyledDataGrid";
import { CustomerFeatureFlagsContext } from "../../../../../contexts/CustomerFeatureFlagsContext";
import SvgToIcon from "../../../../../icons/SvgToIcon";
import { ProtectionLevelText } from "../../../../../utils/constants";
import ChipColor from "../../../../remediation/plan/components/ChipColor";
import { icons } from "../../../../stack/environment/icons/iconExport";
import IntegrationImage from "../../../../stack/integrations/components/IntegrationImage";
import DetectionLabelIcon from "../../../detections/components/DetectionLabelIcon";
import DetectionsTooltip from "../../../detections/components/DetectionsTooltip";
import { getActiveDetections } from "../../../detections/components/utils";
import StepsModalButton from "../../../steps/components/StepsModalButton";
const TopImpactSteps = ({ filteredScenariosSets }) => {
  const colors = useColors();
  const [, setSearchParams] = useSearchParams();
  const [topImpactSteps, setTopImpactSteps] = useState([]);
  const [impactSteps, setImpactSteps] = useState([]);
  const { fetchAllSteps, trackEvent } = useAPI();
  const { detectionsActive } = useContext(CustomerFeatureFlagsContext);

  const { data: steps, isLoading: stepsIsLoading } = useQuery({
    queryKey: stepKeys.all,
    queryFn: fetchAllSteps,
    select: (data) =>
      data.data
        .filter((step) => !step.is_virtual)
        .map((step) => ({ ...step, id: step.step_id })),
  });

  const aggregateStepsValues = (scenarioSets) => {
    const aggregatedSteps = {};

    scenarioSets?.forEach((scenarioSet) => {
      Object.entries(scenarioSet?.steps_info).forEach(([stepId, value]) => {
        if (!aggregatedSteps[stepId]) {
          aggregatedSteps[stepId] = 0;
        }
        aggregatedSteps[stepId] += value;
      });
    });

    return aggregatedSteps;
  };

  useEffect(() => {
    if (filteredScenariosSets?.length > 0 && steps?.length > 0) {
      const aggregatedSteps = aggregateStepsValues(filteredScenariosSets);

      const topSteps = Object.entries(aggregatedSteps).map(
        ([stepId, scenariosCount]) => {
          const step = steps.find((step) => {
            return step.step_id.toString() === stepId;
          });

          return {
            ...step,
            scenariosCount,
          };
        }
      );

      // Filter Missing-controls steps out
      const filteredTopSteps = topSteps.filter(
        (step) => step.protection_level !== ProtectionLevelText.MISSING_CONTROLS
      );

      filteredTopSteps.sort((a, b) => b.scenariosCount - a.scenariosCount);

      setTopImpactSteps(filteredTopSteps.slice(0, 5));
      setImpactSteps(filteredTopSteps);
    } else {
      setTopImpactSteps([]);
      setImpactSteps([]);
    }
  }, [filteredScenariosSets, steps]);

  const columns = [
    {
      field: "id",
      headerName: "ID",
      width: 50,
      renderHeader: (params) => (
        <Typography variant="overline">{params.colDef?.headerName}</Typography>
      ),
      renderCell: (params) => {
        return <Typography variant="caption">#{params.value}</Typography>;
      },
    },
    {
      field: "name",
      headerName: "Step Name",
      flex: 0.6,
      renderHeader: (params) => (
        <Typography variant="overline">{params.colDef?.headerName}</Typography>
      ),
      sortable: false,
      renderCell: (params) => {
        const maxSeverity = params.row?.last_detections
          ? Math.max(
              ...params.row?.last_detections.map(
                (detection) => detection.severity
              )
            )
          : 0;
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
            {detectionsActive && params.row?.is_detected && (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                width={"100%"}
              >
                <DetectionsTooltip
                  content={
                    <Box>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                        }}
                      >
                        <Typography
                          variant="caption"
                          color={colors.common_black}
                        >
                          {getActiveDetections(params.row?.last_detections)
                            .length + " Events detected."}{" "}
                        </Typography>
                        <Typography variant="caption">
                          Highest Severity:
                        </Typography>
                        <ChipColor
                          value={maxSeverity}
                          type={"controlPriority"}
                          size="small"
                          labelWidth={true}
                        />
                      </Box>
                      <Typography variant="caption">
                        Click on the step to view details
                      </Typography>
                    </Box>
                  }
                >
                  <Box>
                    <DetectionLabelIcon />
                  </Box>
                </DetectionsTooltip>
              </Box>
            )}
            <CustomToolTip
              title={params.value?.length > 55 ? params.value : ""}
            >
              <Typography variant="caption">{params.value}</Typography>
            </CustomToolTip>
          </Box>
        );
      },
    },

    {
      field: "mitigating_controls_tools_set",
      headerName: "Tools",
      width: 80,
      align: "left",
      renderHeader: (params) => (
        <Typography variant="overline">{params.colDef?.headerName}</Typography>
      ),
      valueFormatter: (params) => {
        if (Array.isArray(params.value)) {
          return params.value.join(" ");
        }
      },
      renderCell: (params) => {
        return (
          <Box display="flex" justifyContent="center" alignItems="center">
            {params?.row?.cwe_id ? (
              <CustomToolTip title={"This step is associated with CVEs"}>
                <Box sx={{ display: "flex", justifyItems: "center" }}>
                  <SvgToIcon
                    IconLogo={icons.cveIcon}
                    width={"40px"}
                    style={{ padding: "8px" }}
                  />
                </Box>
              </CustomToolTip>
            ) : params?.row?.protection_level ===
              ProtectionLevelText.MISSING_CONTROLS ? (
              <CustomToolTip title={"This step has no protection in place"}>
                <Box sx={{ display: "flex", justifyItems: "center" }}>
                  <SvgToIcon
                    IconLogo={icons.questionMarkIcon}
                    width={"40px"}
                    style={{ padding: "8px" }}
                  />
                </Box>
              </CustomToolTip>
            ) : (
              params?.value?.length > 0 &&
              params?.value
                ?.map((tool, index) => (
                  <IntegrationImage
                    key={`${tool}-${index}`}
                    name={tool}
                    width={"40px"}
                  />
                ))
                .slice(0, 1)
            )}
            {params?.value?.length > 1 && (
              <CustomToolTip
                title={
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    {params?.value?.slice(1).map((tool) => (
                      <IntegrationImage
                        name={tool}
                        width={"18px"}
                        showToolTip={true}
                        newLogo={true}
                      />
                    ))}
                  </Box>
                }
              >
                <Typography variant="body2">
                  {params.value.length > 1
                    ? `+${params.value.length - 1}`
                    : null}
                </Typography>
              </CustomToolTip>
            )}
          </Box>
        );
      },
    },
    {
      headerAlign: "left",
      flex: 0.15,
      sortable: false,
      align: "left",
      headerName: "Impact",
      renderHeader: (params) => (
        <Typography variant="overline">{params.colDef?.headerName}</Typography>
      ),
      renderCell: (params) => {
        const coverageImprovement = Math.round(
          (params.row.scenariosCount /
            filteredScenariosSets
              ?.map((scenarioSet) => scenarioSet.scenario_count)
              .reduce((a, b) => a + b, 0)) *
            100
        );

        return (
          <Box display={"flex"} gap={1} alignItems={"center"}>
            {filteredScenariosSets?.length > 0 && (
              <Box display={"flex"} gap={1} alignItems={"center"}>
                <Typography variant="caption">
                  {coverageImprovement === 0 ? "1" : coverageImprovement}%
                </Typography>
                <NorthRoundedIcon
                  sx={{
                    color: colors.success_400,
                    width: "16px",
                    height: "16px",
                  }}
                />
              </Box>
            )}
          </Box>
        );
      },
    },
  ];

  const handleRowClick = (row) => {
    setSearchParams({ step_id: row.row.id });
    trackEvent("Top Impact Steps Row Clicked - " + row.row.id);
  };

  return (
    <Box
      sx={{
        borderRadius: "6px",
        boxShadow: "0px 0px 2px 1px " + colors.divider2,
        backgroundColor: colors.common_white,
        padding: 2,
        display: "flex",
        flexDirection: "column",
        gap: 1,
      }}
    >
      <Box
        display={"flex"}
        gap={2}
        alignItems={"center"}
        justifyContent={"space-between"}
      >
        <Box display={"flex"} gap={1} alignItems={"center"}>
          <SvgToIcon
            IconLogo={icons.stepsIcon}
            width={"16px"}
            style={{ color: colors.primary_main }}
          />
          <Typography variant="subtitle1">
            {"Top Attack Steps to Mitigate"}
          </Typography>
        </Box>
        <StepsModalButton
          buttonText="View All"
          steps={impactSteps}
          filteredScenariosSets={filteredScenariosSets}
        />
      </Box>
      <StyledDataGrid
        style={{ cursor: "pointer" }}
        rowHeight={40}
        columnHeaderHeight={30}
        columns={columns}
        rows={topImpactSteps}
        onRowClick={handleRowClick}
        hideFooter
        boxSXProps={{
          "& .MuiDataGrid-root": {
            border: "none",
            boxShadow: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .MuiDataGrid-columnHeaders": {
            borderBottom: "none",
          },
          "& .MuiDataGrid-columnSeparator": {
            display: "none",
          },
          "& .MuiDataGrid-columnHeader": {
            cursor: "default",
          },
          border: "none",
        }}
        loading={stepsIsLoading}
      />
    </Box>
  );
};

export default TopImpactSteps;
